import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Dumbbell, Users, Clock, Award, MapPin, Phone, Mail, Trophy, Target, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { GymCarousel } from '../components/GymCarousel';
import { gymInfo } from '../mock';

export const Home = () => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  const whatsappNumber = '51939573726';
  const whatsappContactLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, estoy interesado')}`;
  const whatsappPaymentLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, he realizado el pago y quiero enviar el comprobante')}`;
  const yapeNumber = '+51 933 545 176';

  const features = [
    {
      icon: Dumbbell,
      title: "Equipamiento Premium",
      description: "Tecnología de última generación para tu entrenamiento"
    },
    {
      icon: Users,
      title: "Entrenadores Certificados",
      description: "Profesionales dedicados a tu transformación"
    },
    {
      icon: Clock,
      title: "Horarios Flexibles",
      description: "Abierto casi todo el día para adaptarnos a ti"
    },
    {
      icon: Award,
      title: "Resultados Garantizados",
      description: "Metodología probada y planes personalizados"
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative pt-24 pb-32 px-4 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_nex-fitness/artifacts/tuz4c0io_IMG_4781.JPG%20%281%29.webp" 
            alt="Interior NEX Level Fitness"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/60" />
        </div>
        
        {/* Background gradient with animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-sky-900/20 animate-pulse z-0" style={{ animationDuration: '3s' }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(14,165,233,0.1),transparent_50%)] z-0" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(30,64,175,0.1),transparent_50%)] z-0" />
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center relative">
            {/* Background gym image behind text */}
            <div className="absolute inset-0 -z-10 rounded-3xl overflow-hidden opacity-20">
              <img 
                src="https://customer-assets.emergentagent.com/job_nex-fitness/artifacts/yawynfdf_image.png" 
                alt="Interior NEX Level Fitness"
                className="w-full h-full object-cover blur-sm"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            </div>
            
            <div className="inline-block mb-6 animate-bounce" style={{ animationDuration: '2s' }}>
              <span className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-sky-500/20 border border-sky-500/30 rounded-full text-sky-400 text-sm font-semibold backdrop-blur-sm">
                Bienvenido a NEX LEVEL FITNESS
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight animate-fade-in">
              <span className="bg-gradient-to-r from-white via-sky-100 to-white bg-clip-text text-transparent">
                Transforma Tu
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-sky-400 to-blue-500 bg-clip-text text-transparent">
                Cuerpo y Mente
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {gymInfo.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white font-semibold px-8 py-6 text-lg shadow-2xl shadow-blue-500/30 transition-all duration-300 hover:scale-110 hover:shadow-blue-500/50"
                onClick={() => window.open(whatsappContactLink, '_blank')}
              >
                Contáctanos por WhatsApp
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-sky-500 text-sky-400 hover:bg-sky-500/10 px-8 py-6 text-lg transition-all duration-300 hover:scale-110"
                asChild
              >
                <Link to="/membresias">Ver Membresías</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative elements with animation */}
        <div className="absolute top-40 left-10 w-72 h-72 bg-blue-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
      </section>

      {/* Gym Equipment Carousel */}
      <section className="py-20 px-4 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-950/20 via-transparent to-transparent" />
        
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <span className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-sky-500/20 border border-sky-500/30 rounded-full text-sky-400 text-sm font-semibold backdrop-blur-sm inline-block mb-6">
              NUESTRO EQUIPAMIENTO
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Instalaciones de
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-sky-400 bg-clip-text text-transparent">
                Última Generación
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-12">
              Máquinas profesionales y espacios diseñados para tu máximo rendimiento
            </p>
          </div>

          <GymCarousel />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                ¿Por Qué Elegir
              </span>
              <span className="bg-gradient-to-r from-blue-400 to-sky-400 bg-clip-text text-transparent"> NEX</span>
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">?</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Somos más que un gimnasio, somos tu compañero en la transformación
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-gradient-to-br from-gray-900 to-black border-sky-500/20 hover:border-sky-500/50 transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-sky-500/30 group animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-br from-blue-600 to-sky-500 p-3 rounded-xl w-fit mb-4 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-sky-400 transition-colors duration-300">{feature.title}</h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Director/Founder Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black via-blue-950/10 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzM4YmRmOCIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20" />
        
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <span className="text-sky-400 font-semibold mb-2 block animate-fade-in">NUESTRO DIRECTOR</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-400 to-sky-400 bg-clip-text text-transparent">
                Rodrigo Valle de Paz
              </span>
            </h2>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Trophy className="h-6 w-6 text-yellow-500" />
              <p className="text-xl text-gray-300">Campeón IFBB Pro</p>
              <Trophy className="h-6 w-6 text-yellow-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-sky-500 rounded-2xl blur-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-500" />
              <img 
                src="https://customer-assets.emergentagent.com/job_nex-fitness/artifacts/cg18farx_image.png" 
                alt="Rodrigo Valle de Paz - Campeón Mr. Olympia"
                className="relative rounded-2xl shadow-2xl w-full h-auto transform group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-full font-bold shadow-lg flex items-center space-x-2 animate-bounce" style={{ animationDuration: '2s' }}>
                <Trophy className="h-5 w-5" />
                <span>IFBB Pro</span>
              </div>
            </div>

            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-gray-900 to-black border-sky-500/20 hover:border-sky-500/50 transition-all duration-300 hover:scale-105">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3 mb-4">
                    <Target className="h-6 w-6 text-sky-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Su Transformación</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Coach fitness él junto a su gran equipo van a ayudarte a crear tu mejor versión.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-gray-900 to-black border-sky-500/20 hover:border-sky-500/50 transition-all duration-300 hover:scale-105">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <Trophy className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Logro Profesional</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        En 2017, alcanzó el máximo reconocimiento de su carrera al coronarse <span className="text-sky-400 font-semibold">campeón absoluto</span> en su categoría durante el certamen <span className="text-sky-400 font-semibold">Mr. Olympia Amateur Sudamérica</span> en Medellín, Colombia. Esto le permitió obtener su tarjeta como atleta <span className="text-yellow-500 font-semibold">IFBB Pro</span>.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-blue-600/20 to-sky-500/20 border-sky-500/30 backdrop-blur-sm hover:scale-105 transition-transform duration-300">
                <CardContent className="p-6 text-center">
                  <Flame className="h-8 w-8 text-orange-500 mx-auto mb-3" />
                  <p className="text-white font-semibold text-lg">
                    "La transformación es posible cuando tienes el compromiso y la guía correcta"
                  </p>
                  <p className="text-sky-400 text-sm mt-2">- Rodrigo Valle de Paz</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-right">
              <span className="text-sky-400 font-semibold mb-2 block">SOBRE NOSOTROS</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Tu Centro de
                </span>
                <br />
                <span className="bg-gradient-to-r from-blue-400 to-sky-400 bg-clip-text text-transparent">
                  Transformación Total
                </span>
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed">
                En NEX, no solo ofrecemos un espacio para entrenar. Creamos experiencias transformadoras que van más allá del físico. Nuestro equipo de profesionales certificados está comprometido con tu éxito.
              </p>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Con instalaciones de última generación, programas personalizados y una comunidad motivadora, tenemos todo lo que necesitas para alcanzar tus objetivos de fitness y bienestar.
              </p>
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white font-semibold transition-all duration-300 hover:scale-110"
                asChild
              >
                <Link to="/profesores">
                  Conoce al Equipo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            
            <div className="relative animate-slide-left">
              <div className="aspect-square rounded-2xl overflow-hidden border border-sky-500/30 hover:scale-105 transition-transform duration-500 shadow-2xl shadow-blue-500/20">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nex-fitness/artifacts/3gcsymvk_IMG_4784.JPG%20%281%29.webp" 
                  alt="Interior del gimnasio NEX Level"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-6 -right-6 w-full h-full rounded-2xl bg-gradient-to-br from-blue-600/10 to-sky-500/10 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Visítanos
              </span>
            </h2>
            <p className="text-gray-400 text-lg">
              Estamos aquí para ayudarte en tu viaje fitness
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="bg-gradient-to-br from-gray-900 to-black border-sky-500/20 hover:border-sky-500/50 transition-all duration-300 hover:scale-110">
              <CardContent className="p-6">
                <div className="bg-gradient-to-br from-blue-600 to-sky-500 p-3 rounded-xl w-fit mb-4">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Ubicación</h3>
                <p className="text-gray-400">{gymInfo.address}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-900 to-black border-sky-500/20 hover:border-sky-500/50 transition-all duration-300 hover:scale-110">
              <CardContent className="p-6">
                <div className="bg-gradient-to-br from-blue-600 to-sky-500 p-3 rounded-xl w-fit mb-4">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Teléfono</h3>
                <a href={whatsappContactLink} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-sky-400 transition-colors duration-300">
                  {gymInfo.phone}
                </a>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-900 to-black border-sky-500/20 hover:border-sky-500/50 transition-all duration-300 hover:scale-110">
              <CardContent className="p-6">
                <div className="bg-gradient-to-br from-blue-600 to-sky-500 p-3 rounded-xl w-fit mb-4">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Email</h3>
                <a href={`mailto:${gymInfo.email}`} className="text-gray-400 hover:text-sky-400 transition-colors duration-300">
                  {gymInfo.email}
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Hours */}
          <Card className="mt-8 bg-gradient-to-br from-gray-900 to-black border-sky-500/20 hover:scale-105 transition-transform duration-300">
            <CardContent className="p-8">
              <h3 className="text-2xl font-semibold text-white mb-6 text-center">Horarios de Apertura</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-sky-400 font-semibold mb-2">Lunes - Viernes</p>
                  <p className="text-gray-400">6:00 - 23:00</p>
                </div>
                <div>
                  <p className="text-sky-400 font-semibold mb-2">Sábado</p>
                  <p className="text-gray-400">8:00 - 21:00</p>
                </div>
                <div>
                  <p className="text-sky-400 font-semibold mb-2">Domingo</p>
                  <p className="text-gray-400">9:00 - 20:00</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section with Payment Button */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-sky-500/20 to-blue-600/20 animate-pulse" style={{ animationDuration: '3s' }} />
        <div className="absolute inset-0 backdrop-blur-3xl" />
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">
                ¿Listo Para Comenzar?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Únete a nuestra comunidad y comienza tu transformación hoy mismo
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-110"
                asChild
              >
                <Link to="/membresias">
                  Ver Planes de Membresía
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              
              {/* Payment Button */}
              <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
                <DialogTrigger asChild>
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-110"
                  >
                     Pagar con Yape/Plin
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-gradient-to-br from-gray-900 to-black border-sky-500/30 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-center mb-4">
                      Pagar con Yape/Plin
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6">
                    <div className="text-center">
                      <p className="text-gray-300 mb-4">Realiza tu pago escaneando el código QR o al siguiente número:</p>
                      
                      {/* QR Code Placeholder */}
                      <div className="bg-white p-6 rounded-xl mx-auto w-fit mb-4">
                        <div className="w-48 h-48 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-purple-800 font-bold text-lg mb-2">Yape/Plin</p>
                            <p className="text-purple-900 font-bold text-2xl">{yapeNumber}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4 mb-4">
                        <p className="text-sky-400 font-semibold text-lg">{yapeNumber}</p>
                        <p className="text-gray-400 text-sm mt-1">Número para Yape/Plin</p>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold py-6 text-lg transition-all duration-300 hover:scale-105"
                      onClick={() => {
                        window.open(whatsappPaymentLink, '_blank');
                        setShowPaymentModal(false);
                      }}
                    >
                       Enviar Comprobante por WhatsApp
                    </Button>
                    
                    <p className="text-gray-400 text-sm text-center">
                      Después de realizar el pago, envíanos tu comprobante por WhatsApp para confirmar tu membresía.
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
